import React, { useEffect, useRef, useState } from 'react';


const faqs = [
    {
        q: 'Do I need a visa to visit Kyrgyzstan?',
        a: 'Citizens of over 60 countries — including the US, EU, UK, and most of Asia — can enter Kyrgyzstan visa-free for up to 30 or 60 days. If your country requires a visa, the e-Visa process is simple and takes 3–5 business days. We\'re happy to guide you through it when you book.',
    },
    {
        q: 'Is Kyrgyzstan safe for travelers?',
        a: 'Yes — Kyrgyzstan is considered one of the safest destinations in Central Asia. Crime against tourists is rare. Our guides are with you throughout the trip, and we stay in contact 24/7. As with any destination, we advise standard precautions, and we\'ll brief you fully before departure.',
    },
    {
        q: 'What should I pack?',
        a: 'Layers are key. Even in summer, mountain temperatures drop sharply at night. We recommend: a warm fleece or down jacket, waterproof outer layer, sturdy hiking boots, sunscreen (UV is intense at altitude), and a reusable water bottle. We send a full packing list once your tour is confirmed.',
    },
    {
        q: 'How physically demanding are the tours?',
        a: 'We offer trips for all fitness levels — from easy cultural routes around Bishkek and Karakol to multi-day treks in the Tian Shan mountains. Every tour page shows a difficulty rating. If you\'re unsure, just ask and we\'ll match you to the right itinerary.',
    },
    {
        q: 'What is the best time to visit Kyrgyzstan?',
        a: 'June to September is peak season — warm, green valleys, passable mountain roads, and the famous Nomad festivals. July–August is ideal for high-altitude trekking. May and October offer fewer crowds and stunning colors. Winter tours are possible for those who want a raw, untouched experience.',
    },
    {
        q: 'Are meals and accommodation included?',
        a: 'Most of our tours include accommodation (guesthouses, yurt camps, or local homestays) and at least two meals a day. Exact inclusions are listed on each tour page. Staying with local families is one of the highlights of a Kyrgyz trip — the food is fresh, hearty, and unlike anything you\'ll find elsewhere.',
    },
    {
        q: 'What languages do your guides speak?',
        a: 'Our guides speak English fluently, and most also speak Russian, Kyrgyz, and several European languages including Italian, Spanish, and German. If you need a guide in a specific language, let us know when booking — we\'ll do our best to accommodate.',
    },
    {
        q: 'Can I customize my tour?',
        a: 'Absolutely. Every itinerary we offer can be adjusted to your schedule, group size, interests, and budget. We also build fully custom tours from scratch. Just contact us with your dates and ideas — we\'ll put together a proposal within 24 hours.',
    },
];

const FAQItem = ({ item, isOpen, onToggle }) =>
    <div
        className={`border rounded-2xl overflow-hidden transition-colors duration-300 ${
            isOpen ? 'border-gold/40 bg-dark-card' : 'border-white/10 bg-dark-card hover:border-white/20'
        }`}
    >
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left"
        >
            <span className={`font-unbounded font-semibold text-sm leading-snug transition-colors duration-300 ${isOpen ? 'text-gold' : 'text-white'}`}>
                {item.q}
            </span>
            <span
                className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${
                    isOpen ? 'border-gold bg-gold/10 text-gold rotate-45' : 'border-white/20 text-white/50'
                }`}
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
            </span>
        </button>

        <div
            style={{
                display: 'grid',
                gridTemplateRows: isOpen ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}
        >
            <div style={{ overflow: 'hidden' }}>
                <p className="font-manrope text-white/60 text-sm leading-relaxed px-7 pb-6 pt-1">
                    {item.a}
                </p>
            </div>
        </div>
    </div>;

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => {
                if (e.isIntersecting) e.target.classList.add('visible');
            }),
            { threshold: 0.08 }
        );
        const els = ref.current?.querySelectorAll('.reveal');
        els?.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className="bg-dark-2 py-24 md:py-36">
            <div className="container">
                {/* Header */}
                <div className="reveal flex flex-col items-center gap-4 mb-16 text-center">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-px bg-it-green opacity-90" />
                        <p className="font-unbounded text-white text-xs uppercase tracking-widest">Got questions?</p>
                        <div className="w-8 h-px bg-gold opacity-70" />
                    </div>
                    <h2 className="font-unbounded font-bold text-4xl md:text-5xl text-white">FAQ</h2>
                    <p className="font-manrope text-white/50 text-lg max-w-xl leading-relaxed mt-2">
                        Everything you need to know before your adventure begins.
                    </p>
                </div>

                {/* Two-column layout on desktop */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {faqs.map((item, i) => (
                        <div
                            key={i}
                            className="reveal"
                            style={{ transitionDelay: `${i * 0.06}s` }}
                        >
                            <FAQItem
                                item={item}
                                isOpen={openIndex === i}
                                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FAQ;
